// client/src/components/pages/EmailReports/scheduleUtils.ts
//
// Saving an email report's schedule (ST-00229, RPT-11 / ST-00203), shared by the Quick
// (ReportConfigModal) and Custom (AdvancedReportConfigModal) report modals:
//  - toScheduleTimeInput: the stored schedule time ("HH:MM:SS", pg TIME) as the form's "HH:MM".
//    `new Date('08:00:00')` is an Invalid Date, so the edit form showed NaN:NaN.
//  - saveReportSchedule: POST /email-reports/:id/schedule when scheduling is on, and also when
//    it was switched off on a report whose schedule is active (isActive:false, the settings are
//    kept for re-enabling). Unticking used to skip the call, so the schedule kept sending.
//  - mergeScheduleIntoReport: the saved schedule on the list row, so the page shows the right
//    badge at once (the create / update responses are read before the schedule changes).
import api from '../../../services/api';
import type {
  EmailReportApiError,
  EmailReportListRow,
  EmailReportSchedule,
  ScheduleFormData,
  SchedulePayload,
} from '../../../types/clientPages/emailReports';

export const DEFAULT_SCHEDULE_TIME = '08:00';

const TIME_TYPES = ['daily', 'weekly', 'monthly'];

/**
 * The stored schedule time as the form's "HH:MM": accepts pg TIME "HH:MM:SS", "HH:MM", "H:MM"
 * and a legacy ISO datetime ("1970-01-01T15:42:00.000Z", whose time of day was read in UTC).
 * Anything else gives `fallback`.
 */
export function toScheduleTimeInput(value: unknown, fallback = DEFAULT_SCHEDULE_TIME): string {
  if (typeof value !== 'string') return fallback;
  const m = /(?:^|T)(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?/.exec(value.trim());
  if (!m) return fallback;
  const hours = Number(m[1]);
  const minutes = Number(m[2]);
  if (hours > 23 || minutes > 59) return fallback;
  return `${String(hours).padStart(2, '0')}:${m[2]}`;
}

/** The POST /:id/schedule body: the type, timezone, on/off and only that type's own field. */
export function buildSchedulePayload(data: ScheduleFormData, isActive: boolean): SchedulePayload {
  const payload: SchedulePayload = {
    scheduleType: data.scheduleType,
    timezone: data.timezone,
    isActive,
  };
  if (TIME_TYPES.includes(data.scheduleType)) {
    payload.scheduleTime = data.scheduleTime;
  } else if (data.scheduleType === 'cron') {
    payload.cronExpression = data.cronExpression;
  } else if (data.scheduleType === 'recurring') {
    payload.intervalMinutes = data.intervalMinutes;
  } else if (data.scheduleType === 'once') {
    payload.scheduledDatetime = data.scheduledDatetime;
  }
  return payload;
}

export interface ScheduleSaveResult {
  /** The schedule row the server answered with; null when no call was needed or it failed. */
  schedule: EmailReportSchedule | null;
  /** Why the schedule could not be saved (the report itself was saved). */
  error: string | null;
}

/**
 * Save the schedule after the report itself was saved.
 *  - scheduling ticked: POST the schedule (active);
 *  - unticked, and the report had an ACTIVE schedule: POST it with isActive:false;
 *  - unticked otherwise (new report, or a schedule that is already off): no call.
 */
export async function saveReportSchedule(
  reportId: number,
  data: ScheduleFormData,
  hadActiveSchedule: boolean
): Promise<ScheduleSaveResult> {
  if (!data.isActive && !hadActiveSchedule) return { schedule: null, error: null };
  try {
    const res = await api.emailReports.createSchedule(
      reportId,
      buildSchedulePayload(data, data.isActive)
    );
    return { schedule: (res.data as EmailReportSchedule) ?? null, error: null };
  } catch (err) {
    const e = err as EmailReportApiError;
    return {
      schedule: null,
      error: e.response?.data?.error || e.response?.data?.message || e.message || 'Unknown error',
    };
  }
}

/** The list row with the saved schedule's fields (the badge, schedule column and next run). */
export function mergeScheduleIntoReport<T extends Partial<EmailReportListRow>>(
  report: T,
  schedule: EmailReportSchedule | null
): T {
  if (!schedule) return report;
  const keep = <V>(value: V | undefined, previous: V | null | undefined): V | null =>
    value !== undefined ? value : (previous ?? null);
  return {
    ...report,
    schedule_id: schedule.id ?? report.schedule_id ?? null,
    schedule_type: keep(schedule.schedule_type, report.schedule_type),
    schedule_time: keep(schedule.schedule_time, report.schedule_time),
    cron_expression: keep(schedule.cron_expression, report.cron_expression),
    interval_minutes: keep(schedule.interval_minutes, report.interval_minutes),
    schedule_active: schedule.is_active,
    next_run_time: schedule.is_active ? keep(schedule.next_run_time, report.next_run_time) : null,
  };
}
